import { useState } from 'react';
import { z } from 'zod';

import { useTranslation } from '../i18n';

interface IUseFormValidationReturn<T> {
  values: T;
  errors: Partial<Record<keyof T, string>>;
  handleChange: (field: keyof T, value: T[keyof T]) => void;
  handleSubmit: (onSubmit: (data: T) => Promise<void>) => Promise<void>;
  reset: (defaults?: Partial<T>) => void;
  isSubmitting: boolean;
}

const useFormValidation = <T extends Record<string, unknown>>(
  schema: z.ZodSchema<T>,
  initialValues: T,
): IUseFormValidationReturn<T> => {
  const { t } = useTranslation();
  const [values, setValues] = useState<T>(initialValues);
  const [errors, setErrors] = useState<Partial<Record<keyof T, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (field: keyof T, value: T[keyof T]) => {
    setValues((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next[field];
      return next;
    });
  };

  const resolveMessage = (message: string): string => {
    // If the message starts with "validation." it's an i18n key
    if (message.startsWith('validation.')) {
      const translated = t(message);
      return translated !== message ? translated : message;
    }
    return message;
  };

  const handleSubmit = async (onSubmit: (data: T) => Promise<void>) => {
    const result = schema.safeParse(values);

    if (!result.success) {
      const fieldErrors: Partial<Record<keyof T, string>> = {};

      for (const issue of result.error.issues) {
        const field = issue.path[0] as keyof T;

        if (field && !fieldErrors[field]) {
          fieldErrors[field] = resolveMessage(issue.message);
        }
      }

      setErrors(fieldErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      await onSubmit(result.data);
    } finally {
      setIsSubmitting(false);
    }
  };

  const reset = (defaults?: Partial<T>) => {
    setValues(defaults ? { ...initialValues, ...defaults } : initialValues);
    setErrors({});
  };

  return {
    values,
    errors,
    handleChange,
    handleSubmit,
    reset,
    isSubmitting,
  };
};

export default useFormValidation;
